import { designToken, quickProperty } from '../component-tokens';
import { sizeAttributes } from './utils';
import { borderBase, focusState } from './common';
const chipAttrs = {
    cursor: quickProperty('pointer'),
    display: quickProperty('inline-flex'),
    'align-items': quickProperty('center'),
    'justify-content': quickProperty('center'),
    color: designToken('type-color-default'),
    gap: designToken('spacing-02'),
    'border-width': quickProperty('1px'),
    'border-style': quickProperty('solid'),
    'border-color': quickProperty('transparent'),
    'box-sizing': quickProperty('border-box'),
    font: designToken('body-small'),
    'border-radius': quickProperty('15px'),
    height: quickProperty('1.875rem'),
    padding: quickProperty('4px 12px'),
    'min-width': quickProperty('5rem'),
    outline: quickProperty('none'),
    'background-color': designToken('interactive-color-neutral-default'),
};
const defaultStates = [
    {
        alias: 'chip-hover',
        name: 'hover',
        attributes: {
            'background-color': designToken('interactive-color-neutral-hover'),
        },
    },
    {
        alias: 'chip-focus',
        name: 'focus',
        attributes: Object.assign(Object.assign({}, borderBase), focusState),
    },
    {
        alias: 'chip-active',
        name: 'active',
        attributes: {
            'background-color': designToken('interactive-color-neutral-pressed'),
            'box-shadow': quickProperty('none'),
        },
    },
];
const text = {
    alias: 'chip-text',
    name: 'text',
    content: 'Default Chip',
    tag: 'span',
    attributes: {
        margin: quickProperty('0 10px'),
    },
};
export const chipTokens = {
    name: 'chip',
    reactImports: 'Chip',
    attributes: chipAttrs,
    description: `
  Chips are compact elements that represent an input, attribute, or action. They allow users to enter information, make selections, filter content, or trigger actions. Chips should appear dynamically as a group of multiple interactive elements.
Gutter spacing (row & column) is 8px for both default & small chip sizes.
Chip text is displayed in sentence case.`,
    sizes: [
        {
            name: 'small',
            attributes: {
                height: quickProperty('1.5rem'),
            },
            children: [
                {
                    name: 'icon-svg',
                    attributes: Object.assign(Object.assign({}, sizeAttributes('0.75rem')), { 'margin-left': quickProperty('-2px'), 'margin-right': quickProperty('4px') }),
                },
                {
                    alias: 'chip-small-text',
                    name: 'text',
                    attributes: {
                        margin: quickProperty('0 0.5rem'),
                    },
                },
                {
                    name: 'icon',
                    attributes: Object.assign(Object.assign({}, sizeAttributes('spacing-04', designToken)), { '--goto-icon-size': designToken('spacing-04') }),
                },
            ],
        },
    ],
    variants: [
        {
            name: 'default',
            states: defaultStates,
            children: [text],
        },
        {
            name: 'icon',
            attributes: Object.assign(Object.assign({}, sizeAttributes('spacing-05', designToken)), { '--goto-icon-size': designToken('spacing-05'), '--goto-icon-color': designToken('icon-color-secondary'), color: designToken('type-color-secondary') }),
        },
        {
            name: 'interactive-icon',
            attributes: {
                '--goto-icon-color': designToken('icon-color-inverted'),
                color: designToken('type-color-inverted'),
            },
        },
        {
            name: 'disabled-icon',
            attributes: {
                '--goto-icon-color': designToken('icon-color-disabled'),
                color: designToken('type-color-disabled'),
            },
        },
        {
            alias: 'chip-selected',
            name: 'selected',
            attributes: Object.assign(Object.assign({}, borderBase), { 'border-color': quickProperty('transparent'), color: designToken('type-color-inverted'), 'background-color': designToken('interactive-color-brand-default'), 'box-shadow': quickProperty('none') }),
            children: [
                Object.assign({}, text),
            ],
            states: [
                {
                    name: 'hover',
                    attributes: {
                        'background-color': designToken('interactive-color-brand-hover'),
                    },
                },
                {
                    name: 'active',
                    attributes: {
                        'background-color': designToken('interactive-color-brand-pressed'),
                    },
                },
                {
                    name: 'focus',
                    attributes: Object.assign({}, focusState),
                },
            ],
        },
        {
            alias: 'chip-error',
            name: 'error',
            attributes: Object.assign(Object.assign({}, borderBase), { 'border-color': quickProperty('transparent'), color: designToken('type-color-inverted'), 'background-color': designToken('interactive-color-danger-default'), 'box-shadow': quickProperty('none') }),
            children: [
                Object.assign({}, text),
            ],
            states: [
                {
                    name: 'hover',
                    attributes: {
                        'background-color': designToken('interactive-color-danger-hover'),
                    },
                },
                {
                    name: 'active',
                    attributes: {
                        'background-color': designToken('interactive-color-danger-pressed'),
                    },
                },
                {
                    name: 'focus',
                    attributes: {
                        'background-color': designToken('interactive-color-danger-default'),
                        'border-color': designToken('focus-border-color-danger'),
                        'box-shadow': designToken('focus-color-highlight-danger'),
                    },
                },
            ],
        },
        {
            alias: 'chip-disabled',
            name: 'disabled',
            attributes: {
                color: designToken('type-color-disabled'),
                // TODO: apply correct one when we migrate chip to FAST
                'background-color': designToken('interactive-color-disabled'),
                cursor: quickProperty('default'),
                'box-shadow': quickProperty('none'),
            },
            children: [
                Object.assign({}, text),
            ],
        },
    ],
};
